import { Link } from "@tanstack/react-router";
import { Phone, MessageCircle, Calendar, Mail, MapPin, Wrench, ArrowRight } from "lucide-react";
import { getBusinessConfig, areas as ALL_AREAS, services as ALL_SERVICES } from "@/lib/site-config";
import { getWhatsAppUrl, trackEvent } from "@/lib/tracking";

/**
 * Prominent CTA block for area + area/service pages.
 * Surfaces /book, /contact, the parent service hub, and neighbouring areas.
 */
interface Props {
  areaSlug: string;
  areaName: string;
  serviceSlug?: string;
  serviceName?: string;
  /** Optional override list of neighbouring area slugs. Defaults to a sensible subset. */
  neighbours?: string[];
}

// Best-effort neighbour map for our covered East London areas.
const NEIGHBOURS: Record<string, string[]> = {
  poplar: ["canary-wharf", "limehouse", "blackwall", "mile-end"],
  "canary-wharf": ["poplar", "isle-of-dogs", "blackwall", "limehouse"],
  "isle-of-dogs": ["canary-wharf", "poplar", "blackwall", "limehouse"],
  limehouse: ["poplar", "shadwell", "wapping", "mile-end"],
  docklands: ["canary-wharf", "isle-of-dogs", "canning-town", "blackwall"],
  "tower-hamlets": ["bow", "mile-end", "bethnal-green", "stepney"],
  whitechapel: ["aldgate", "shadwell", "stepney", "brick-lane"],
  "brick-lane": ["whitechapel", "bethnal-green", "aldgate", "shadwell"],
  bow: ["mile-end", "bethnal-green", "stratford", "tower-hamlets"],
  "mile-end": ["bow", "stepney", "bethnal-green", "limehouse"],
  "bethnal-green": ["brick-lane", "bow", "mile-end", "whitechapel"],
  wapping: ["shadwell", "limehouse", "aldgate", "whitechapel"],
  stratford: ["bow", "canning-town", "mile-end", "blackwall"],
  stepney: ["whitechapel", "mile-end", "shadwell", "limehouse"],
  shadwell: ["whitechapel", "wapping", "limehouse", "stepney"],
  aldgate: ["whitechapel", "wapping", "brick-lane", "shadwell"],
  "canning-town": ["blackwall", "stratford", "docklands", "canary-wharf"],
  blackwall: ["canary-wharf", "poplar", "isle-of-dogs", "canning-town"],
};

export function AreaPageCTAs({ areaSlug, areaName, serviceSlug, serviceName, neighbours }: Props) {
  const biz = getBusinessConfig();
  const wa = getWhatsAppUrl(serviceName ?? serviceSlug ?? `Garage near ${areaName}`, {
    area: areaName,
  });
  const parent = serviceSlug ? ALL_SERVICES.find((s) => s.slug === serviceSlug) : null;
  const neighbourSlugs = (neighbours ?? NEIGHBOURS[areaSlug] ?? []).filter(
    (s) => s !== areaSlug && ALL_AREAS.some((a) => a.slug === s),
  ).slice(0, 4);

  // Book deep-link with prefilled service + area (URL params win over sessionStorage).
  const bookSearch: { service?: string; area?: string } = {};
  if (serviceSlug) bookSearch.service = serviceSlug;
  if (areaName) bookSearch.area = areaName;

  return (
    <section className="py-10 md:py-14 bg-card border-y border-border">
      <div className="mx-auto max-w-5xl px-4">
        <div className="rounded-2xl border border-border bg-background p-5 md:p-7 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="eyebrow text-primary">Talk to us today</p>
              <h2 className="mt-1 text-2xl md:text-3xl font-bold text-foreground">
                {serviceName ? `Book ${serviceName} near ${areaName}` : `Book your visit from ${areaName}`}
              </h2>
              <p className="mt-2 text-sm md:text-base text-muted-foreground max-w-xl">
                Call, WhatsApp, request a booking online, or send us a question — we usually reply the same day.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 shrink-0">
              <a
                href={biz.phoneHref}
                onClick={() => trackEvent("call_click", { location: "area_cta", area: areaSlug })}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <Phone className="h-4 w-4" /> Call
              </a>
              <a
                href={wa}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("whatsapp_click", {
                  location: "area_cta",
                  area: areaSlug,
                  service: serviceSlug || "none",
                })}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#25D366] px-3 py-3 text-sm font-bold text-white hover:bg-[#1ea952] transition-colors"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
              <Link
                to="/book"
                search={bookSearch}
                onClick={() => trackEvent("service_cta_click", {
                  location: "area_cta",
                  area: areaSlug,
                  service: serviceSlug || "none",
                })}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-foreground px-3 py-3 text-sm font-bold text-background hover:opacity-90 transition-opacity"
              >
                <Calendar className="h-4 w-4" /> Book
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-border bg-background px-3 py-3 text-sm font-bold text-foreground hover:border-primary/40 transition-colors"
              >
                <Mail className="h-4 w-4" /> Contact
              </Link>
            </div>
          </div>

          {(parent || neighbourSlugs.length > 0) && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {parent && (
                <Link
                  to={`/${parent.slug}` as any}
                  className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3 hover:border-primary/50 transition-colors"
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <Wrench className="h-4 w-4 text-primary" />
                    All about {parent.name} →
                  </span>
                  <ArrowRight className="h-4 w-4 text-primary" />
                </Link>
              )}
              {neighbourSlugs.length > 0 && (
                <div className="rounded-lg border border-border bg-card px-4 py-3">
                  <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5 text-primary" /> Nearby areas
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {neighbourSlugs.map((slug) => {
                      const a = ALL_AREAS.find((x) => x.slug === slug)!;
                      return serviceSlug ? (
                        <Link
                          key={slug}
                          to="/areas/$area/$service"
                          params={{ area: slug, service: serviceSlug }}
                          className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-semibold text-foreground hover:border-primary/50 hover:text-primary transition-colors"
                        >
                          {a.name}
                          <ArrowRight className="h-3 w-3" />
                        </Link>
                      ) : (
                        <Link
                          key={slug}
                          to="/areas/$area"
                          params={{ area: slug }}
                          className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-semibold text-foreground hover:border-primary/50 hover:text-primary transition-colors"
                        >
                          {a.name}
                          <ArrowRight className="h-3 w-3" />
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
